import React from "react";
import { motion } from "framer-motion";
import { Layers3 } from "lucide-react";

const technologies = [
  {
    name: "React",
    category: "Frontend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
  },
  {
    name: "Next.js",
    category: "Frontend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg",
  },
  {
    name: "Tailwind CSS",
    category: "Frontend",
    logo: "https://www.vectorlogo.zone/logos/tailwindcss/tailwindcss-icon.svg",
  },
  {
    name: "JavaScript",
    category: "Frontend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
  },
  {
    name: "Node.js",
    category: "Backend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
  },
  {
    name: "Express.js",
    category: "Backend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg",
  },
  {
    name: "React Native",
    category: "Mobile",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
  },
  {
    name: "Flutter",
    category: "Mobile",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg",
  },
  {
    name: "MongoDB",
    category: "Database",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
  },
  {
    name: "MySQL",
    category: "Database",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
  },
  {
    name: "PHP",
    category: "Backend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg",
  },
  {
    name: "Python",
    category: "AI & Backend",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
  },
  {
    name: "WordPress",
    category: "CMS",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-plain.svg",
  },
  {
    name: "AWS",
    category: "Cloud",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
  },
  {
    name: "Cloudflare",
    category: "Cloud",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cloudflare/cloudflare-original.svg",
  },
  {
    name: "Docker",
    category: "DevOps",
    logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
  },
];

const categories = [
  "All",
  "Frontend",
  "Backend",
  "Mobile",
  "Database",
  "AI & Backend",
  "Cloud",
  "DevOps",
  "CMS",
];

export default function TechStack() {
  const [active, setActive] = React.useState("All");

  const filtered =
    active === "All"
      ? technologies
      : technologies.filter((tech) => tech.category === active);

  return (
    <div className="relative">

      <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-pink-500/10">
              <Layers3 className="h-4 w-4 text-pink-400" />
            </div>

            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-300">
              Technology Ecosystem
            </span>
          </div>

          <h3 className="text-2xl font-black text-white">
            Built With Modern Technology
          </h3>

          <p className="mt-2 max-w-xl text-sm text-slate-400">
            A flexible technology stack engineered for performance,
            scalability, security and intelligent digital experiences.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
          16 technologies
        </div>
      </div>

      <div className="mb-8 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActive(category)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-all ${
              active === category
                ? "border-pink-400/40 bg-gradient-to-r from-pink-500/20 to-violet-500/20 text-pink-200 shadow-lg shadow-pink-500/10"
                : "border-white/10 bg-white/[0.04] text-slate-500 hover:border-white/20 hover:text-slate-300"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      <motion.div
        layout
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
      >
        {filtered.map((tech) => (
          <motion.div
            layout
            key={tech.name}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            whileHover={{ y: -5 }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] p-4 transition-all duration-300 hover:border-pink-500/30 hover:bg-white/[0.08] hover:shadow-[0_15px_40px_rgba(236,72,153,0.10)]"
          >
            <div className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-pink-500/10 blur-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            <div className="relative flex items-center gap-3">

              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-xl bg-pink-500/20 blur-md opacity-0 transition-opacity group-hover:opacity-100" />

                <div className="relative grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white p-2.5 shadow-lg">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    className="h-full w-full object-contain transition duration-300 group-hover:scale-110"
                  />
                </div>
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-black text-white">
                  {tech.name}
                </p>

                <p className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-slate-500">
                  {tech.category}
                </p>
              </div>

            </div>

            <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-pink-500/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
          </motion.div>
        ))}
      </motion.div>

      <div className="mt-8 flex items-center justify-center gap-2 text-center">
        <span className="text-xs text-slate-500">
          Choosing the right technology for every business challenge
        </span>
      </div>
    </div>
  );
}